/*
 * Разбор текста сообщения на кусочки: обычный текст, упоминание, ссылка.
 *
 * Разбор, а не разметка. Чужой текст остаётся текстом: он никогда не
 * превращается в HTML, из него лишь выделяются куски, которые страница рисует
 * своими составляющими. Одно криво написанное сообщение не должно становиться
 * чужим сценарием на экране у всех читающих.
 *
 * Ссылки нужны потому, что без них адрес в переписке был просто буквами.
 * Телефон подчёркивал его сам и предлагал перейти, но внутри установленного
 * приложения такие самораспознанные ссылки открываются через раз.
 */

export interface TextSegment {
	kind: 'text';
	text: string;
}

export interface MentionSegment {
	kind: 'mention';
	text: string;
	/** Упомянут ли на самом деле: сервер прислал список задетых людей. */
	active: boolean;
}

export interface LinkSegment {
	kind: 'link';
	text: string;
	href: string;
}

export type MessageSegment = TextSegment | MentionSegment | LinkSegment;

/**
 * Адрес в тексте.
 *
 * Только `http`/`https` и «www.»: адрес без схемы человек пишет именно так, а
 * всё остальное — `javascript:`, `data:`, `file:` — ссылкой не станет никогда,
 * даже если написать его буквами.
 */
const LINK = /\b(?:https?:\/\/|www\.)[^\s<>"']+/giu;

const MENTION = /@[a-z0-9._-]{3,32}/giu;

/** Знаки, которые почти всегда относятся к фразе, а не к адресу. */
const TRAILING = /[.,!?;:)\]}»"'…]+$/u;

/**
 * Разложить текст на кусочки.
 *
 * Совпадения ищутся одним проходом по обоим видам: иначе упоминание внутри
 * адреса (а такое бывает в путях) разорвало бы ссылку пополам.
 */
export function messageSegments(
	text: string,
	options: { mentionOf?: (login: string) => boolean } = {}
): MessageSegment[] {
	if (!text) return [];

	const found = [
		...[...text.matchAll(LINK)].map((match) => ({ match, kind: 'link' as const })),
		...[...text.matchAll(MENTION)].map((match) => ({ match, kind: 'mention' as const }))
	]
		.filter((item) => typeof item.match.index === 'number')
		.sort((first, second) => (first.match.index ?? 0) - (second.match.index ?? 0));

	const segments: MessageSegment[] = [];
	let cursor = 0;

	for (const { match, kind } of found) {
		const start = match.index ?? 0;
		// Пересечения пропускаем: первый нашедшийся кусок побеждает.
		if (start < cursor) continue;
		if (start > cursor) segments.push({ kind: 'text', text: text.slice(cursor, start) });

		let value = match[0];
		if (kind === 'link') {
			/*
			 * Точка или скобка в конце — это конец фразы, а не часть адреса.
			 * Возвращаем их в текст, иначе ссылка ведёт не туда.
			 */
			const tail = TRAILING.exec(value)?.[0] ?? '';
			if (tail) value = value.slice(0, value.length - tail.length);

			segments.push({
				kind: 'link',
				text: value,
				// Телефон сам ставит заглавную в начале фразы: «Www.сайт.ру» без схемы
				// браузер счёл бы путём внутри приложения и открыл бы его же.
				href: /^www\./iu.test(value) ? `https://${value}` : value
			});
			cursor = start + value.length;
			continue;
		}

		segments.push({
			kind: 'mention',
			text: value,
			active: options.mentionOf?.(value.slice(1).toLocaleLowerCase()) ?? false
		});
		cursor = start + value.length;
	}

	if (cursor < text.length) segments.push({ kind: 'text', text: text.slice(cursor) });
	return segments.length > 0 ? segments : [{ kind: 'text', text }];
}
